import { FC, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { prepareMinMaxRange } from '@app/helpers/lineChartsHelper';
import { IAccountDynamicsLine } from '@entities/accountDynamics/IAccountDynamicsLine';
import { InputCheckbox } from '@shared/uikit/input-checkbox';
import useAccountDynamicsGraph from '@pages/AccountsDynamics/hooks/useAccountDynamicsGraph';
import { Filter } from '@pages/AccountsDynamics/List/filter';
import EmptyList from '@widgets/empty-list';
import { ListLoader } from '@shared/uikit/loader';

interface ILinesChartProps {
  filter: Filter;
  search: string;
}

const AccountDynamicsLinesChart: FC<ILinesChartProps> = ({ filter, search }) => {
  const [mining, setMining] = useState<boolean>(true);
  const [agent, setAgent] = useState<boolean>(true);
  const [isNewOld, setNewOld] = useState<boolean>(false);

  const [loadingGraphData, graphData] = useAccountDynamicsGraph(isNewOld, filter, search);

  const prepareDomain = (data: IAccountDynamicsLine[]) => {
    let dataMin = 'dataMin';
    let dataMax = 'dataMax';

    if ((!agent && !mining) || !data.length) return [dataMin, dataMax];

    let values: number[] = [];
    if (mining) values = data.map(item => item.mining);
    if (agent) values = data.map(item => item.referral);

    let min: number = Math.min(...values),
      max: number = Math.max(...values);

    if (0 === min && 0 === max) return [dataMin, dataMax];

    return prepareMinMaxRange(min, max);
  };

  return !loadingGraphData ? (
    <>
      {graphData.length ? (
        <>
          <div className="flex justify-between mb-[20px]">
            <div className="flex flex-wrap gap-x-[20px] gap-y-[5px] pr-[30px] pt-[15px]">
              <InputCheckbox
                value={isNewOld}
                onChange={value => {
                  setNewOld(value);
                  setMining(true);
                  setAgent(true);
                }}
                label="Новый / Старый"
              />
            </div>
            <div className="flex flex-wrap gap-x-[20px] gap-y-[5px] pr-[30px] pt-[15px]">
              <InputCheckbox
                value={mining}
                onChange={value => {
                  setMining(value);
                }}
                label={isNewOld ? 'Новый' : 'Майнинг аккаунты'}
                bgCheckedColor="#12BC37"
                isRounded={true}
                withCheckmark={true}
              />
              <InputCheckbox
                value={agent}
                onChange={value => {
                  setAgent(value);
                }}
                label={isNewOld ? 'Старый' : 'Агентские аккаунты'}
                bgCheckedColor="#45DBE6"
                isRounded={true}
                withCheckmark={true}
              />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={graphData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                {mining ? (
                  <linearGradient id="mining" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#12BC37" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#12BC37" stopOpacity={0} />
                  </linearGradient>
                ) : null}

                {agent ? (
                  <linearGradient id="referral" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#45DBE6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#45DBE6" stopOpacity={0} />
                  </linearGradient>
                ) : null}
              </defs>

              <XAxis dataKey="name" interval={0} tickLine={false} />
              {agent && mining ? (
                <YAxis />
              ) : (
                <YAxis
                  width={70}
                  type="number"
                  domain={prepareDomain(graphData)}
                  allowDataOverflow
                />
              )}
              {152 > graphData.length ? <CartesianGrid opacity={0.1} color="#5E5E5E33" /> : null}
              <Tooltip
                contentStyle={{
                  backgroundColor: '#211F24',
                  borderRadius: '8px',
                  border: 'none',
                }}
              />
              <Area
                type="natural"
                name={'Дата'}
                dataKey="date"
                stroke="white"
                stackId={213}
                fillOpacity={1}
              />

              {mining ? (
                <Area
                  dataKey="mining"
                  name={isNewOld ? 'Новый' : 'Майнинг аккаунты'}
                  unit={' Ph'}
                  stroke="#12BC37"
                  fillOpacity={1}
                  fill="url(#mining)"
                />
              ) : null}

              {agent ? (
                <Area
                  dataKey="referral"
                  name={isNewOld ? 'Старый' : 'Агентские аккаунты'}
                  stroke="#45DBE6"
                  unit={' Ph'}
                  fillOpacity={1}
                  fill="url(#referral)"
                />
              ) : null}
            </AreaChart>
          </ResponsiveContainer>
        </>
      ) : (
        <div className="mt-[20px]">
          <EmptyList />
        </div>
      )}
    </>
  ) : (
    <ListLoader loading={true} />
  );
};

export default AccountDynamicsLinesChart;
